import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "gradatim-cookie-consent";

/**
 * GDPR-aware cookie banner.
 * - Loads GA4 only AFTER user accepts.
 * - Persists choice in localStorage.
 */
export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Defer to next tick so it animates in after first paint
    const t = setTimeout(() => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        setVisible(true);
      } else if (saved === "accepted") {
        loadGA4();
      }
    }, 800);
    return () => clearTimeout(t);
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    loadGA4();
    setVisible(false);
  };

  const reject = () => {
    localStorage.setItem(STORAGE_KEY, "rejected");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[100]"
          data-testid="cookie-banner"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="relative bg-white rounded-2xl shadow-2xl border border-hairline p-5 md:p-6">
            <button
              type="button"
              onClick={reject}
              aria-label="Dismiss cookie banner"
              className="absolute top-3 right-3 p-1 rounded-md text-slate-400 hover:text-ink transition-colors"
              data-testid="cookie-banner-close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="overline text-copper mb-2">We value your privacy</div>
            <p className="text-[13.5px] text-slate-600 leading-relaxed pr-6">
              We use cookies and similar tools to understand how you use our
              site and improve your experience.{" "}
              <Link to="/privacy" className="text-copper hover:underline">
                Read more
              </Link>
              .
            </p>
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                type="button"
                onClick={reject}
                data-testid="cookie-banner-reject"
                className="flex-1 px-4 py-2.5 text-[13px] font-medium tracking-wide text-slate-600 hover:text-ink border border-hairline rounded-md transition-colors"
              >
                Decline
              </button>
              <button
                type="button"
                onClick={accept}
                data-testid="cookie-banner-accept"
                className="flex-1 px-4 py-2.5 text-[13px] font-medium tracking-wide text-white bg-ink hover:bg-brand-900 rounded-md transition-colors"
              >
                Accept all
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/**
 * Load Google Analytics 4. Only called after user consent.
 * Reads measurement ID from REACT_APP_GA4_ID. Silent no-op if not set.
 */
function loadGA4() {
  const id = process.env.REACT_APP_GA4_ID;
  if (!id || !id.startsWith("G-")) return;

  // Don't inject twice
  if (document.getElementById("ga4-loader")) return;

  const s = document.createElement("script");
  s.id = "ga4-loader";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  const inline = document.createElement("script");
  inline.id = "ga4-init";
  inline.text = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${id}', { anonymize_ip: true });
  `;
  document.head.appendChild(inline);
}
